import { ArchiveIcon, ChevronRightIcon, Loader2Icon, LockIcon, type LucideIcon } from "lucide-react"
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
  type RefObject,
} from "react"
import { Link, useLocation } from "react-router"
import { Badge } from "@/primitives/badge.tsx"
import { Separator } from "@/primitives/separator.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { IdentityTile } from "./identity.tsx"
import { useCopied } from "./key.tsx"
import {
  documentTitleOf,
  stickyTopOf,
  type ChromeLayer,
  type MountedChrome,
  type StickyTop,
} from "./page-chrome.ts"
import { handsBack, takesFocus, type FocusAt, type FocusFrom } from "./page-scroll.ts"

/*
 * Every page's frame, top to bottom: a PageHeader naming where the Member is, an optional
 * PageToolbar holding the page's filters and view options, and a PageBody at one of six widths.
 *
 * The header and toolbar stick to the top of the page's one scroll container, and a sticky header
 * inside a list (a table's head, a GroupHeader) must stick under them. Rather than each list
 * guessing, the header and toolbar register themselves in PageChromeContext as they mount, and a
 * list asks `useStickyTop()` where to stick.
 */

type PageChrome = { top: StickyTop; register: (layer: ChromeLayer) => () => void }

/**
 * What chrome the page has mounted. Outside a `PageScroll` nothing registers, and a list sticks
 * under the header, which every page has.
 */
export const PageChromeContext = createContext<PageChrome>({
  top: "top-12",
  register: () => () => undefined,
})

/**
 * The inset panel's one scroll container, and the context the chrome inside it registers in. The
 * shell renders one around the routed page; nothing else in the panel scrolls vertically, except a
 * Board's own scroller.
 *
 * It is what the keyboard scrolls, too: it takes focus as each page arrives, unless something else
 * has it (`takesFocus`), without moving, and without a ring, since it is the page and not a control
 * on it. Tab goes on from it into the page, starting at the header.
 */
export function PageScroll({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState<MountedChrome>({ header: 0, toolbar: 0 })
  const register = useCallback((layer: ChromeLayer) => {
    setMounted((now) => ({ ...now, [layer]: now[layer] + 1 }))
    return () => setMounted((now) => ({ ...now, [layer]: now[layer] - 1 }))
  }, [])
  const chrome = useMemo(() => ({ top: stickyTopOf(mounted), register }), [mounted, register])
  const scroller = useRef<HTMLDivElement>(null)
  const onFocus = useKeyboardScroll(scroller)

  return (
    <PageChromeContext value={chrome}>
      <div
        ref={scroller}
        data-slot="page-scroll"
        tabIndex={-1}
        onFocus={onFocus}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto outline-none"
      >
        {children}
      </div>
    </PageChromeContext>
  )
}

/**
 * Makes `scroller` what the keyboard scrolls: it takes focus as each page arrives, unless something
 * else has it, and takes it back from an overlay closing on the focus it held. Gives back the
 * handler for its focus events.
 */
function useKeyboardScroll(scroller: RefObject<HTMLDivElement | null>) {
  const { pathname } = useLocation()
  // The path focus inside the page was last taken on, so a link followed from the page before
  // is told apart from a field the new page focused as it opened.
  const focusedOn = useRef(pathname)
  // Whether the scroller itself holds the page's focus, rather than a control on the page.
  const heldItself = useRef(false)

  // After the page's own effects, so a field it focuses as it opens already has focus.
  useEffect(() => {
    const element = scroller.current
    if (element && takesFocus(focusAt(element, focusedOn.current), pathname))
      element.focus({ preventScroll: true })
  }, [scroller, pathname])

  return (event: FocusEvent<HTMLDivElement>) => {
    const element = event.currentTarget
    // A page's dialogs are its children in React but portalled out of it in the DOM, so focus in
    // them is not the page's.
    if (!element.contains(event.target)) return
    const from = focusFrom(event.relatedTarget)
    if (event.target !== element && handsBack(heldItself.current, from)) {
      element.focus({ preventScroll: true })
      return
    }
    heldItself.current = event.target === element
    focusedOn.current = pathname
  }
}

/** Where focus is now, as `takesFocus` asks, `page` being the page scroll. */
function focusAt(page: Element, focusedOn: string): FocusAt {
  const active = document.activeElement
  if (!active || active === document.body) return "nowhere"
  if (page.contains(active)) return { page: focusedOn }
  // Chrome focuses a link the pointer clicks, but without the ring it shows the keyboard's focus.
  return active.matches("a[href]:not(:focus-visible)") ? "clicked" : "elsewhere"
}

/** Where focus came from: an overlay (Base UI portals every one), or nowhere, as from one gone. */
const focusFrom = (previous: EventTarget | null): FocusFrom =>
  previous === null ||
  (previous instanceof Element && previous.closest("[data-base-ui-portal]") !== null)
    ? "overlay"
    : "elsewhere"

/** The `top-*` class an in-list sticky header takes, so it sticks under the page's chrome. */
export function useStickyTop() {
  return useContext(PageChromeContext).top
}

/** Registers a layer of chrome for as long as it is mounted, before the page paints. */
function useRegistered(layer: ChromeLayer) {
  const { register } = useContext(PageChromeContext)
  useLayoutEffect(() => register(layer), [register, layer])
}

/**
 * One step of the breadcrumb. `href` makes it a link back up (with `state` passed on, such as the
 * Board a ticket was opened from); `glyph` puts a Project's tile or an icon before its label;
 * `count` follows it, as in "Members 68"; `copy` makes it a button that copies that value, as a
 * ticket's key does.
 */
export type Crumb = {
  label: string
  href?: string
  state?: unknown
  glyph?: { kind: "project"; projectKey: string } | { kind: "icon"; icon: LucideIcon }
  count?: number
  copy?: string
}

/** A tab in the header: a link to one of a page's sibling views, such as a Project's Settings. */
export type HeaderTab = { label: string; href: string; current: boolean }

const crumbClass =
  "flex h-7 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-ui font-medium transition-colors duration-100 ease-snappy focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"

function CrumbContent({ crumb }: { crumb: Crumb }) {
  const { glyph } = crumb
  return (
    <>
      {glyph?.kind === "project" && <IdentityTile projectKey={glyph.projectKey} size={16} />}
      {glyph?.kind === "icon" && <glyph.icon aria-hidden className="size-4 shrink-0" />}
      <span className="truncate">{crumb.label}</span>
      {crumb.count !== undefined && (
        <span className="text-xs text-subtle-foreground tabular-nums">{crumb.count}</span>
      )}
    </>
  )
}

function CopyCrumb({ crumb, value }: { crumb: Crumb; value: string }) {
  const { copied, copy } = useCopied(value)
  return (
    <Tooltip>
      <TooltipTrigger
        closeOnClick={false}
        onClick={copy}
        className={cn(crumbClass, "cursor-pointer text-foreground hover:bg-accent")}
      >
        <CrumbContent crumb={crumb} />
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied" : `Copy ${value}`}</TooltipContent>
    </Tooltip>
  )
}

function CrumbItem({ crumb, last }: { crumb: Crumb; last: boolean }) {
  if (crumb.copy !== undefined) return <CopyCrumb crumb={crumb} value={crumb.copy} />
  if (crumb.href && !last)
    return (
      <Link
        to={crumb.href}
        state={crumb.state}
        className={cn(crumbClass, "text-muted-foreground hover:bg-accent hover:text-foreground")}
      >
        <CrumbContent crumb={crumb} />
      </Link>
    )
  return (
    <span
      aria-current={last ? "page" : undefined}
      className={cn(crumbClass, last ? "text-foreground" : "text-muted-foreground")}
    >
      <CrumbContent crumb={crumb} />
    </span>
  )
}

const headerBadges = {
  archived: { variant: "neutral", Icon: ArchiveIcon, label: "Archived" },
  "read-only": { variant: "outline", Icon: LockIcon, label: "Read only" },
} as const

/**
 * The top of every page, 48px tall and stuck there: the breadcrumb naming where the Member is,
 * any `badges` saying the page can't be changed, the page's sibling `tabs`, and its `actions` at
 * the end (small Buttons, the primary one carrying its Kbd). There is no page title besides the
 * last crumb, which is the page's h1 at the crumbs' size.
 *
 * It names the browser tab after the crumbs, most specific first, "Members · Beacon";
 * `documentTitle` names it instead, as a ticket's key and title do, and Beacon still follows.
 *
 * `heading="body"` is for a page whose body holds its hero title (a ticket's, an Epic's), which is
 * then the page's one h1, so the last crumb is plain text rather than a second.
 *
 * While `pending` (a refetch keeping the old figures, on a page with no toolbar to say it), a
 * spinner leads the actions, as it leads a PageToolbar's end.
 */
export function PageHeader({
  crumbs,
  tabs,
  badges = [],
  actions,
  pending = false,
  documentTitle,
  heading = "last-crumb",
}: {
  crumbs: readonly Crumb[]
  tabs?: readonly HeaderTab[]
  badges?: readonly (keyof typeof headerBadges)[]
  actions?: ReactNode
  pending?: boolean
  documentTitle?: string
  /** Which is the page's h1: its last crumb (the default), or a hero title in its body. */
  heading?: "last-crumb" | "body"
}) {
  useRegistered("header")
  const title = documentTitleOf(crumbs, documentTitle)
  useEffect(() => {
    document.title = title
  }, [title])

  return (
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-border-subtle bg-background px-4">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-0.5">
        {crumbs.map((crumb, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <ChevronRightIcon aria-hidden className="size-3.5 shrink-0 text-subtle-foreground" />
            )}
            {index === crumbs.length - 1 ? (
              // The last crumb is the page's name, so it is the page's one h1, for a screen
              // reader's heading list and for a route change to land on, at the crumb's own size;
              // unless the body's hero title is that h1.
              heading === "last-crumb" ? (
                <h1 className="flex min-w-0">
                  <CrumbItem crumb={crumb} last />
                </h1>
              ) : (
                <CrumbItem crumb={crumb} last />
              )
            ) : (
              <CrumbItem crumb={crumb} last={false} />
            )}
          </Fragment>
        ))}
      </nav>
      {badges.length > 0 && (
        <div className="ml-1 flex shrink-0 items-center gap-1.5">
          {badges.map((badge) => {
            const { variant, Icon, label } = headerBadges[badge]
            return (
              <Badge key={badge} variant={variant}>
                <Icon aria-hidden />
                {label}
              </Badge>
            )
          })}
        </div>
      )}
      {tabs && tabs.length > 0 && (
        <>
          <Separator orientation="vertical" className="mx-1 h-4 data-vertical:self-center" />
          <nav aria-label="Views" className="flex shrink-0 items-center gap-1">
            {tabs.map((tab) => (
              <Link
                key={tab.href}
                to={tab.href}
                aria-current={tab.current ? "page" : undefined}
                className={cn(
                  "inline-flex h-7 items-center rounded-md px-2 text-ui font-medium transition-colors duration-100 ease-snappy focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                  tab.current
                    ? "bg-selected text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
        </>
      )}
      {(actions || pending) && (
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {pending && <Updating />}
          {actions}
        </div>
      )}
    </header>
  )
}

/**
 * The one spinner that says a refetch is under way while the old rows or figures stay on screen:
 * a PageHeader's, a PageToolbar's and a card DataTable's bar. Internal to the design system.
 */
export function Updating() {
  return (
    <Loader2Icon
      role="status"
      aria-label="Updating"
      className="size-3.5 animate-spin text-subtle-foreground"
    />
  )
}

/**
 * The bar under the PageHeader, 40px tall and stuck under it: filters, their chips and search at
 * the `start`, and the count and Display at the `end`. While `pending` (a refetch keeping the old
 * rows), a spinner leads the end.
 */
export function PageToolbar({
  start,
  end,
  pending = false,
}: {
  start?: ReactNode
  end?: ReactNode
  pending?: boolean
}) {
  useRegistered("toolbar")
  return (
    <div
      role="toolbar"
      className="sticky top-12 z-20 flex h-10 shrink-0 items-center gap-2 border-b border-border-subtle bg-background px-6"
    >
      {start && <div className="flex min-w-0 items-center gap-2">{start}</div>}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {pending && <Updating />}
        {end}
      </div>
    </div>
  )
}

/*
 * The six page widths. The PageHeader's crumbs sit at one x on every page, whatever the width, so
 * moving between pages never shifts the chrome.
 */
const widths = {
  /** Full bleed; rows own their `px-6`. Projects, My tickets, Members. */
  list: "w-full",
  /** Full bleed, filling the panel below the chrome for the Board's own scroller. */
  board: "flex min-h-0 flex-1 flex-col",
  /**
   * Full bleed, side by side: its children are PagePanes. A ticket, Project settings, Groups. It
   * fills what the panel has left below the chrome, and never shrinks below its content: the
   * sticky panes stick only as far as the body they sit in runs, so it must run the page's length.
   */
  split: "flex grow shrink-0",
  /** A column of prose and settings. Connections, Agent, Not found. */
  document: "mx-auto w-full max-w-3xl px-10 py-8",
  /** Wider, for a page of cards. Home. */
  wide: "mx-auto w-full max-w-5xl px-10 py-8",
  /** Widest, for tiles and charts. Spend. */
  dashboard: "mx-auto w-full max-w-6xl px-8 py-6",
} as const

/**
 * A page's content, under its chrome, at one of six widths. `dimmed` fades it while a refetch
 * keeps the previous data on screen. It fades in as it arrives.
 */
export function PageBody({
  width,
  dimmed = false,
  children,
}: {
  width: keyof typeof widths
  dimmed?: boolean
  children: ReactNode
}) {
  return (
    <div
      data-slot="page-body"
      className={cn(
        "animate-in transition-opacity duration-150 fade-in",
        widths[width],
        dimmed && "opacity-70",
      )}
    >
      {children}
    </div>
  )
}

/*
 * The panes of a `split` body. `nav` sticks under the header beside the scrolling `main`. A `rail`
 * runs the page's height, so its border rules the whole page, while what it holds sticks under the
 * header. A `master` list runs the page's height beside its detail, its border with it, while its
 * list sticks under the header at the height the panel shows there and scrolls on its own, so a
 * list longer than the screen can be read to its end without scrolling the detail to its end.
 */
const panes = {
  nav: "w-48 shrink-0 sticky top-12 self-start pt-8 pl-6",
  master: "w-72 shrink-0 border-r border-border-subtle",
  main: "min-w-0 flex-1",
  rail: "w-72 shrink-0 border-l border-border-subtle",
} as const

/** One pane of a `split` PageBody, by the part it plays. */
export function PagePane({ role, children }: { role: keyof typeof panes; children: ReactNode }) {
  return (
    <div data-slot="page-pane" data-role={role} className={panes[role]}>
      {role === "master" ? (
        <div className="sticky top-12 h-below-header overflow-y-auto overscroll-contain">
          {children}
        </div>
      ) : role === "rail" ? (
        <div className="sticky top-12 px-4 py-6">{children}</div>
      ) : (
        children
      )}
    </div>
  )
}
